import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { X, Save, Plus, Trash2, DollarSign, Route, Truck } from 'lucide-react';

// ─── helpers ──────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 10);

function rulesToRows(rules) {
  const priceRows = Object.entries(rules?.deliveryValue ?? {}).map(
    ([senderUid, { price, reward }]) => ({
      id: uid(), senderUid, price: String(price), reward: String(reward),
    })
  );
  const directRows = Object.entries(rules?.deliveryForwarding?.directDeliveries ?? {}).map(
    ([senderUid, courierUid]) => ({ id: uid(), senderUid, courierUid })
  );
  const groupRows = Object.entries(rules?.deliveryForwarding?.deliveryGroups ?? {}).map(
    ([senderUid, uids]) => ({
      id: uid(), senderUid,
      courierGroup: Array.isArray(uids) ? uids.join(', ') : uids,
    })
  );
  return { priceRows, directRows, groupRows };
}

function rowsToRules(priceRows, directRows, groupRows) {
  const deliveryValue = {};
  priceRows.forEach(({ senderUid, price, reward }) => {
    if (senderUid.trim())
      deliveryValue[senderUid.trim()] = { price: Number(price) || 0, reward: Number(reward) || 0 };
  });
  const directDeliveries = {};
  directRows.forEach(({ senderUid, courierUid }) => {
    if (senderUid.trim()) directDeliveries[senderUid.trim()] = courierUid.trim();
  });
  const deliveryGroups = {};
  groupRows.forEach(({ senderUid, courierGroup }) => {
    if (senderUid.trim())
      deliveryGroups[senderUid.trim()] = courierGroup.split(',').map((s) => s.trim()).filter(Boolean);
  });
  return { deliveryValue, deliveryForwarding: { directDeliveries, deliveryGroups } };
}

// ─── member autocomplete cell ─────────────────────────────────────────────────

const filter = createFilterOptions();

function MemberAutocomplete({ value, onChange, members = [], disabled, placeholder }) {
  const options = members.map((m) => ({
    label: m.name || m.email || m.userId,
    sublabel: m.name ? (m.email || '') : '',
    value: m.userId,
  }));

  return (
    <Autocomplete
      freeSolo
      size="small"
      disabled={disabled}
      value={value}
      options={options}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        return filtered;
      }}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.value)}
      renderOption={(props, opt) => (
        <Box component="li" {...props} sx={{ flexDirection: 'column', alignItems: 'flex-start !important' }}>
          <Typography variant="body2" fontWeight={500}>{opt.label}</Typography>
          {opt.sublabel && (
            <Typography variant="caption" color="text.secondary">{opt.sublabel}</Typography>
          )}
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>
            {opt.value}
          </Typography>
        </Box>
      )}
      onInputChange={(_, newVal) => onChange(newVal)}
      onChange={(_, newVal) => {
        if (!newVal) { onChange(''); return; }
        onChange(typeof newVal === 'string' ? newVal : newVal.value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={placeholder}
          inputProps={{ ...params.inputProps, style: { fontSize: '0.82rem' } }}
        />
      )}
    />
  );
}

// ─── editable table ───────────────────────────────────────────────────────────

function EditableTable({ columns, rows, onChangeRow, onDeleteRow, onAddRow, disabled, members }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            {columns.map((col) => (
         <TableCell key={col.field} sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, width: col.width }}>
  {col.label}
</TableCell>
            ))}
            <TableCell width={40} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center"
                sx={{ py: 2, color: 'text.disabled', fontSize: '0.8rem' }}>
                Ei rivejä
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              {columns.map((col) => (
                <TableCell key={col.field} sx={{ py: 0.5, px: 1 }}>
                  {col.useMember ? (
                    <MemberAutocomplete
                      value={row[col.field] ?? ''}
                      onChange={(val) => onChangeRow(row.id, col.field, val)}
                      members={members}
                      disabled={disabled}
                      placeholder={col.placeholder || col.label}
                    />
                  ) : (
                    <TextField
                      value={row[col.field] ?? ''}
                      onChange={(e) => onChangeRow(row.id, col.field, e.target.value)}
                      disabled={disabled}
                      size="small"
                      variant="standard"
                      placeholder={col.placeholder || col.label}
                      inputProps={{ style: { fontSize: '0.82rem' } }}
                      fullWidth
                    />
                  )}
                </TableCell>
              ))}
              <TableCell sx={{ py: 0.5, px: 0.5 }}>
                <IconButton size="small" onClick={() => onDeleteRow(row.id)}
                  disabled={disabled} color="error" sx={{ p: 0.5 }}>
                  <Trash2 size={14} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button size="small" startIcon={<Plus size={14} />} onClick={onAddRow}
          disabled={disabled} sx={{ fontSize: '0.78rem' }}>
          Lisää rivi
        </Button>
      </Box>
    </TableContainer>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

/**
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   rules      organizationData.rules
 *   members    organizationData.members  ← new
 *   onSave     async (rules) => void
 */
export function DeliveryRulesModal({ open, onClose, rules, members = [], onSave }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [priceRows, setPriceRows]   = useState([]);
  const [directRows, setDirectRows] = useState([]);
  const [groupRows, setGroupRows]   = useState([]);

  useEffect(() => {
    if (open) {
      const rows = rulesToRows(rules);
      setPriceRows(rows.priceRows);
      setDirectRows(rows.directRows);
      setGroupRows(rows.groupRows);
      setError(null);
    }
  }, [open, rules]);

  const changeRow = (setter) => (id, field, value) =>
    setter((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const deleteRow = (setter) => (id) =>
    setter((rows) => rows.filter((r) => r.id !== id));

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      await onSave(rowsToRules(priceRows, directRows, groupRows));
      onClose();
    } catch (err) {
      setError(err.message || 'Tallennus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Truck size={20} />
            <Typography variant="h6" fontWeight={600}>Kuljetusmäärittelyt</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={loading}>
            <X size={18} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {/* Hintamäärittely */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <DollarSign size={16} />
            <Typography variant="subtitle1" fontWeight={600}>Hintamäärittely</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Lähettäjäkohtainen toimitushinta ja kuskin palkkio
          </Typography>
          <EditableTable
         columns={[
    { field: 'senderUid', label: 'Lähettäjä', placeholder: 'uid...', useMember: true, width: '60%' },
    { field: 'price',     label: 'Hinta (€)',  placeholder: '0',      width: '20%' },
    { field: 'reward',    label: 'Palkkio (€)', placeholder: '0',     width: '20%' },
  ]}

            rows={priceRows}
            onChangeRow={changeRow(setPriceRows)}
            onDeleteRow={deleteRow(setPriceRows)}
            onAddRow={() => setPriceRows((r) => [...r, { id: uid(), senderUid: '', price: '', reward: '' }])}
            disabled={loading}
            members={members}
          />
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Kuljetusohjaus */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Route size={16} />
            <Typography variant="subtitle1" fontWeight={600}>Kuljetusohjaus</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Määritä mitkä kuljettajat hoitavat kunkin lähettäjän toimitukset
          </Typography>

          {/* Suora */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary"
              sx={{ display: 'block', mb: 0.75 }}>
              SUORA — yksi lähettäjä → yksi kuski
            </Typography>
            <EditableTable
              columns={[
                { field: 'senderUid',  label: 'Lähettäjä',    placeholder: 'uid...', useMember: true },
                { field: 'courierUid', label: 'Kuski',         placeholder: 'uid...', useMember: true },
              ]}
              rows={directRows}
              onChangeRow={changeRow(setDirectRows)}
              onDeleteRow={deleteRow(setDirectRows)}
              onAddRow={() => setDirectRows((r) => [...r, { id: uid(), senderUid: '', courierUid: '' }])}
              disabled={loading}
              members={members}
            />
          </Box>

          {/* Rajattu */}
          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary"
              sx={{ display: 'block', mb: 0.75 }}>
              RAJATTU — yksi lähettäjä → kuskijoukko
            </Typography>
            <EditableTable
              columns={[
                { field: 'senderUid',    label: 'Lähettäjä',  placeholder: 'uid...', useMember: true },
                { field: 'courierGroup', label: 'Kuskijoukko', placeholder: 'uid1, uid2, uid3...' },
              ]}
              rows={groupRows}
              onChangeRow={changeRow(setGroupRows)}
              onDeleteRow={deleteRow(setGroupRows)}
              onAddRow={() => setGroupRows((r) => [...r, { id: uid(), senderUid: '', courierGroup: '' }])}
              disabled={loading}
              members={members}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading} startIcon={<X size={16} />}>
          Peruuta
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Save size={16} />}
        >
          Tallenna määrittelyt
        </Button>
      </DialogActions>
    </Dialog>
  );
}